import { I18N } from 'aurelia-i18n';
import { translate } from 'app/shared/lib/i18n';
import { autoinject } from 'aurelia-framework';
import { validate } from 'app/shared/elements/ess-rule';
import { IDialog } from 'app/shared/elements/ess-dialog';
import { EnhancedElement } from 'app/shared/lib/aurelia';
import { focusFirst } from 'app/shared/lib/focus';
import { guid } from 'app/shared/lib/guid';

export interface IConfirmationDialogResult {
    reason: string | null;
    dialogResponse: ConfirmationOptions;
}

interface DeferredPromise {
    promise: any | null;
    resolve: any | null;
    reject: any | null;
}

// INFO [Matt] These are bitwise flags
export enum ConfirmationOptions {
    YES = 1 << 0, // 1
    NO = 1 << 1, // 2
    OK = 1 << 2, // 4
    CANCEL = 1 << 3, // 8
    OKCancel = OK | CANCEL,
    YesNo = YES | NO,
    StandardConfirm = OKCancel,
}

interface IConfirmationAction {
    label: string;
    option: ConfirmationOptions;
    isSubmit: boolean;
}

export const confirmation = {
    options: ConfirmationOptions,
    showCustom: async (message: string | null = null, subMessage: string | null = null, options?: ConfirmationOptions, reasonRequired?: boolean | false): Promise<IConfirmationDialogResult> => {
        const modalNode = (document.getElementById('globalConfirmModal') as any).au.controller.viewModel as EssConfirmCustomElement; //.au.controller.viewModel as EssConfirm;

        if(modalNode) {
            let result = await modalNode.showCustom(message, subMessage, options || ConfirmationOptions.NO | ConfirmationOptions.YES, reasonRequired);
            focusFirst(document.getElementById('globalConfirmModal') as EnhancedElement);
            return await (result.promise as Promise<any>).then((item: IConfirmationDialogResult) => item);
        }
        return Promise.reject();
    },
    showCancel: async (modelKey: string, count: string | number = 1, requireReason: boolean = false, reasonMaxLength: number | null = null): Promise<IConfirmationDialogResult> => {
        const modalNode = (document.getElementById('globalConfirmModal') as any).au.controller.viewModel as EssConfirmCustomElement; //.au.controller.viewModel as EssConfirm;

        if(modalNode) {
            let result = await modalNode.showConfirm('ui:messages.actions.cancel', modelKey, count, requireReason, reasonMaxLength);
            focusFirst(document.getElementById('globalConfirmModal') as EnhancedElement);
            return await (result.promise as Promise<any>).then((item: IConfirmationDialogResult) => item);
        }
        return Promise.reject();
    },
    showDeactivate: async (modelKey: string, count: string | number = 1, requireReason: boolean = false, reasonMaxLength: number | null = null): Promise<IConfirmationDialogResult> => {
        const modalNode = (document.getElementById('globalConfirmModal') as any).au.controller.viewModel as EssConfirmCustomElement; //.au.controller.viewModel as EssConfirm;

        if(modalNode) {
            let result = await modalNode.showConfirm('ui:messages.actions.deactivate', modelKey, count, requireReason, reasonMaxLength);
            focusFirst(document.getElementById('globalConfirmModal') as EnhancedElement);
            return await (result.promise as Promise<any>).then((item: IConfirmationDialogResult) => item);
        }
        return Promise.reject();
    },
    showDelete: async (modelKey: string, count: string | number = 1, requireReason: boolean = false, reasonMaxLength: number | null = null): Promise<IConfirmationDialogResult> => {
        const modalNode = (document.getElementById('globalConfirmModal') as any).au.controller.viewModel as EssConfirmCustomElement; //.au.controller.viewModel as EssConfirm;

        if(modalNode) {
            let result = await modalNode.showConfirm('ui:messages.actions.delete', modelKey, count, requireReason, reasonMaxLength);
            focusFirst(document.getElementById('globalConfirmModal') as EnhancedElement);
            return await (result.promise as Promise<any>).then((item: IConfirmationDialogResult) => item);
        }
        return Promise.reject();
    },
    showCancelChanges: async (modelKey?: string, count: string | number = 1, requireReason: boolean = false, reasonMaxLength: number | null = null): Promise<IConfirmationDialogResult> => {
        const modalNode = (document.getElementById('globalConfirmModal') as any).au.controller.viewModel as EssConfirmCustomElement; //.au.controller.viewModel as EssConfirm;

        if(modalNode) {
            let result = await modalNode.showConfirm('ui:messages.prompts.cancelChanges', modelKey, count, requireReason, reasonMaxLength);
            focusFirst(document.getElementById('globalConfirmModal') as EnhancedElement);
            return await (result.promise as Promise<any>).then((item: IConfirmationDialogResult) => item);
        }
        return Promise.reject();
    }
}


//need to add some styling to this component as right now it is too big for it's function
@autoinject
export class EssConfirmCustomElement {
    $dialogModal!: IDialog;
    actions: IConfirmationAction[] = [];
    question?: string | null = null;
    additionalInfo?: string | null = null;
    reasonGiven: string | null = null;
    reasonMaxLength: number | null = null;
    result: any;
    au: any;
    reason: boolean = false;

    protected validate = validate;
    protected $isValid: boolean = true;

    protected id: guid = guid();
    protected descriptionId: string | null = null;

    constructor(private readonly i18n: I18N) { }

    public async showConfirm(messageKey: string, modelKey?: string, count: string | number = 1, requireReason: boolean = false, reasonMaxLength: number | null = 50): Promise<any> {
        const message = translate(this.i18n, {
            key: messageKey,
            count: count,
            what: modelKey
        });

        return this.showCustom(message, null, ConfirmationOptions.StandardConfirm, requireReason, reasonMaxLength);
    }

    public async showCustom(
        message: string | null = null,
        subMessage: string | null = null,
        options: ConfirmationOptions = ConfirmationOptions.StandardConfirm,
        reasonRequired: boolean = false,
        reasonMaxLength: number | null = null): Promise<any> {

        this.reason = reasonRequired;
        this.reasonMaxLength = reasonMaxLength;

        if(message) {
            this.question = message;
        } else {
            this.question = 'Are you sure?';
        }
        this.additionalInfo = subMessage;

        //INFO[MW]: This sets the `aria-describedby` for the modal to the subMessage/additionalInfo content, but this may not be a good use of an `aria-description`, or even necessary, for a Confirm modal.  Probability for refactor: high.
        this.descriptionId = this.additionalInfo ? this.id : null;

        this.setActions(options);

        this.$dialogModal.open();
        this.result = this.defer();
        return this.result;
    }

    private setActions(options: ConfirmationOptions): void {
        this.actions = [];

        if(options & ConfirmationOptions.OK) {
            this.actions.push({
                option: ConfirmationOptions.OK,
                label: ConfirmationOptions[ConfirmationOptions.OK],
                isSubmit: true
            });
        }

        if(options & ConfirmationOptions.YES) {
            this.actions.push({
                option: ConfirmationOptions.YES,
                label: ConfirmationOptions[ConfirmationOptions.YES],
                isSubmit: true
            });
        }

        if(options & ConfirmationOptions.NO) {
            this.actions.push({
                option: ConfirmationOptions.NO,
                label: ConfirmationOptions[ConfirmationOptions.NO],
                isSubmit: false
            });
        }

        if(options & ConfirmationOptions.CANCEL) {
            this.actions.push({
                option: ConfirmationOptions.CANCEL,
                label: ConfirmationOptions[ConfirmationOptions.CANCEL],
                isSubmit: false
            });
        }
    }

    private defer(): DeferredPromise {
        let deferred: DeferredPromise = {
            promise: null,
            resolve: null,
            reject: null
        };

        deferred.promise = new Promise((resolve, reject) => {
            deferred.resolve = resolve;
            deferred.reject = reject;
        });

        return deferred;
    }

    protected selectAction(action: IConfirmationAction, event: Event): void {
        if(action.isSubmit && this.reason && !this.reasonGiven) {
            //will want to replace this with a toast, validation display and focus on element or something better
            //than the ugly alert box;
            alert('Please provide a reason');
        }
        else {
            this.result.resolve({
                reason: this.reasonGiven,
                dialogResponse: action.option
            } as IConfirmationDialogResult);

            this.$dialogModal.close();
            this.actions = [];
            this.reasonGiven = null;
        }
        event.stopPropagation();
    }
}
